import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useNewsItem } from '../hooks/useNews';
import { Seo } from '../components/Seo';

function parseYoutubeTimecode(t: string): number {
  // "123" or "123s"
  const simple = t.match(/^(\d+)s?$/);
  if (simple) return parseInt(simple[1]);
  // "1h23m45s", "1h30m", "45s", etc.
  const hms = t.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/);
  if (!hms) return 0;
  return (parseInt(hms[1] ?? '0') * 3600) + (parseInt(hms[2] ?? '0') * 60) + parseInt(hms[3] ?? '0');
}

function youtubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    let videoId: string | null = null;
    let t: string | null = null;

    if (u.hostname === 'youtu.be') {
      videoId = u.pathname.slice(1);
      t = u.searchParams.get('t');
    } else if (u.hostname.includes('youtube.com')) {
      videoId = u.searchParams.get('v');
      t = u.searchParams.get('t');
    }

    if (!videoId) return null;
    const embed = new URL(`https://www.youtube.com/embed/${videoId}`);
    if (t) {
      const start = parseYoutubeTimecode(t);
      if (start > 0) embed.searchParams.set('start', String(start));
    }
    return embed.toString();
  } catch {
    return null;
  }
}

function YoutubeEmbed({ url, label }: { url: string; label: string }) {
  const embedUrl = youtubeEmbedUrl(url);
  if (!embedUrl) return null;
  return (
    <div>
      <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-3">{label}</p>
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={embedUrl}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={label}
        />
      </div>
    </div>
  );
}

const mdComponents = {
  h1: (p: React.HTMLAttributes<HTMLHeadingElement>) => <h1 className="font-display text-2xl font-bold text-mafia-paper mt-6 mb-3" {...p} />,
  h2: (p: React.HTMLAttributes<HTMLHeadingElement>) => <h2 className="font-display text-xl font-bold text-mafia-paper mt-5 mb-2" {...p} />,
  h3: (p: React.HTMLAttributes<HTMLHeadingElement>) => <h3 className="font-display text-lg font-semibold text-mafia-gold mt-4 mb-2" {...p} />,
  p: (p: React.HTMLAttributes<HTMLParagraphElement>) => <p className="text-mafia-paper-dim leading-relaxed mb-4" {...p} />,
  ul: (p: React.HTMLAttributes<HTMLUListElement>) => <ul className="list-disc list-inside text-mafia-paper-dim space-y-1 mb-4 pl-2" {...p} />,
  ol: (p: React.HTMLAttributes<HTMLOListElement>) => <ol className="list-decimal list-inside text-mafia-paper-dim space-y-1 mb-4 pl-2" {...p} />,
  li: (p: React.HTMLAttributes<HTMLLIElement>) => <li className="leading-relaxed" {...p} />,
  strong: (p: React.HTMLAttributes<HTMLElement>) => <strong className="text-mafia-paper font-semibold" {...p} />,
  em: (p: React.HTMLAttributes<HTMLElement>) => <em className="text-mafia-paper-dim italic" {...p} />,
  blockquote: (p: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="border-l-2 border-mafia-gold pl-4 italic text-mafia-paper-dim my-4" {...p} />
  ),
  code: (p: React.HTMLAttributes<HTMLElement>) => (
    <code className="bg-mafia-smoke text-mafia-gold px-1.5 py-0.5 font-mono text-sm rounded" {...p} />
  ),
  hr: () => <OrnamentDivider className="my-6" />,
  a: (p: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a className="text-mafia-gold hover:text-mafia-gold-light underline underline-offset-2 transition-colors" {...p} />
  ),
};

export function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: item, isLoading, isError } = useNewsItem(id ?? '');
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  if (isLoading) {
    return (
      <PageWrapper>
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-mafia-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  if (isError || !item) {
    return (
      <PageWrapper>
        <p className="text-mafia-paper-dim">Новость не найдена.</p>
        <Link to="/news" className="text-mafia-gold hover:underline mt-4 inline-block font-mono text-sm">
          ← Все новости
        </Link>
      </PageWrapper>
    );
  }

  const date = new Date(item.publishedAt).toLocaleDateString('ru-RU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <PageWrapper className="max-w-3xl">
      <Seo title={item.title} description={item.teaser.replace(/[#*_`\[\]]/g, '').slice(0, 160)} path={`/news/${id}`} />

      <Link to="/news" className="font-mono text-xs text-mafia-muted hover:text-mafia-gold tracking-wider uppercase transition-colors mb-8 inline-block">
        ← Все новости
      </Link>

      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">◆ Новости ◆</p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-3 capitalize">{item.title}</h1>
      <p className="font-mono text-xs text-mafia-muted tracking-widest uppercase mb-8 capitalize">{date}</p>

      <OrnamentDivider className="mb-8" />

      {/* Анонс */}
      <div className="mb-10">
        <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {item.teaser}
        </Markdown>
      </div>

      {/* Видео */}
      {(item.videoUrl ?? item.videoUrl2) && (
        <div className="mb-10">
          <YoutubeEmbed url={item.videoUrl ?? item.videoUrl2!} label="Видео · После раздачи ролей" />
        </div>
      )}

      {/* Разбор игры (спойлеры) */}
      {item.breakdown && (
        <div className="border border-mafia-border">
          <button
            onClick={() => setBreakdownOpen((v) => !v)}
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-mafia-smoke transition-colors"
          >
            <span className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase">
              ◆ Разбор игры
            </span>
            <span className="font-mono text-xs text-mafia-muted tracking-wider uppercase flex items-center gap-2">
              {breakdownOpen ? 'Скрыть' : 'Показать'}
              <span className="text-mafia-gold">{breakdownOpen ? '▲' : '▼'}</span>
            </span>
          </button>
          {breakdownOpen && (
            <div className="px-6 pb-6 pt-2 border-t border-mafia-border">
              <p className="font-mono text-xs text-mafia-muted tracking-wider uppercase mb-4">
                ⚠ Осторожно — спойлеры
              </p>
              <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {item.breakdown}
              </Markdown>
            </div>
          )}
        </div>
      )}
    </PageWrapper>
  );
}
